import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import { usePayments, useVoidPayment, PAYMENT_STATUS_LABEL, PAYMENT_METHOD_LABEL, PaymentStatus, paymentClientName } from "@/api/payments";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorMessage } from "@/api/client";
import RegisterPaymentModal from "./RegisterPaymentModal";

const STATUS_STYLE: Record<PaymentStatus, string> = {
  REGISTRADO: "bg-green-100 text-green-700",
  ANULADO: "bg-red-100 text-red-700",
};

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

export default function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = usePayments({ page, pageSize: 15 });
  const voidPayment = useVoidPayment();

  const payments = data?.data ?? [];
  const meta = data?.meta;

  const handleVoid = async (id: string) => {
    if (!window.confirm("¿Anular este pago? Las facturas afectadas volverán a su saldo pendiente.")) return;
    try {
      await voidPayment.mutateAsync(id);
      toast.success("Pago anulado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Pagos</h1>
          <p className="text-sm text-gray-500">Pagos de clientes aplicados a una o más facturas.</p>
        </div>
        {hasPermission("payments.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Registrar pago
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">N.°</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Método</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Monto</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Facturas</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && payments.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  No hay pagos registrados.
                </td>
              </tr>
            )}
            {payments.map((payment) => (
              <tr key={payment.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-500">{payment.paymentNumber}</td>
                <td className="px-4 py-3 text-gray-600">
                  {payment.client ? (
                    <Link to={`/clients/${payment.client.id}`} className="hover:text-brand-600">
                      {paymentClientName(payment)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{payment.paymentDate}</td>
                <td className="px-4 py-3 text-gray-600">{PAYMENT_METHOD_LABEL[payment.method]}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{currency.format(Number(payment.amount))}</td>
                <td className="px-4 py-3 text-gray-600">
                  {payment.allocations.map((a) => `N.° ${a.invoice?.invoiceNumber ?? "—"}`).join(", ")}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[payment.status]}`}>
                    {PAYMENT_STATUS_LABEL[payment.status]}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {payment.status === "REGISTRADO" && hasPermission("payments.update") && (
                    <button type="button" className="text-xs font-medium text-red-500 hover:text-red-700" onClick={() => handleVoid(payment.id)}>
                      Anular
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} pagos)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button className="btn-secondary" disabled={!!meta.totalPages && page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <RegisterPaymentModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
